import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useBudget } from '../../context/BudgetContext';
import { addDebt, removeDebt } from '../../context/budgetActions';
import { formatCAD } from '../../utils/formatCurrency';

/* ── Mobile debt card (replaces table row on xs) ── */
const DebtCard = ({ debt, dispatch, t }) => (
  <Box
    sx={{
      border: '1px solid #EEF0F3',
      borderRadius: '12px',
      p: 2,
      mb: 1.5,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 1,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A', mb: 0.5 }}>
        {debt.creditor}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
            {t('budgetPlanner.debt.balance', 'Saldo')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', fontFeatureSettings: "'tnum'" }}>
            {formatCAD(debt.balance)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
            {t('budgetPlanner.debt.rate', 'Tasa')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
            {debt.rate}%
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
            {t('budgetPlanner.debt.minPayment', 'Pago mín.')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', fontFeatureSettings: "'tnum'" }}>
            {formatCAD(debt.minPayment)}
          </Typography>
        </Box>
      </Box>
    </Box>
    <IconButton
      size="small"
      onClick={() => dispatch(removeDebt(debt.id))}
      color="error"
      aria-label="Eliminar deuda"
      sx={{ mt: 0.5, minWidth: 44, minHeight: 44 }}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

export default function DebtTable() {
  const { t } = useTranslation();
  const { state, dispatch } = useBudget();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({ creditor: '', balance: '', rate: '', minPayment: '' });

  const handleAdd = () => {
    if (!form.creditor.trim() || !form.balance || !form.rate || !form.minPayment) return;
    dispatch(addDebt({
      creditor: form.creditor.trim(),
      balance: Number(form.balance),
      rate: Number(form.rate),
      minPayment: Number(form.minPayment),
    }));
    setForm({ creditor: '', balance: '', rate: '', minPayment: '' });
  };

  return (
    <>
      {/* Mobile: card layout */}
      {isMobile ? (
        <Box>
          {state.debts.map((debt) => (
            <DebtCard key={debt.id} debt={debt} dispatch={dispatch} t={t} />
          ))}
        </Box>
      ) : (
      /* Desktop: table layout */
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('budgetPlanner.debt.creditor', 'Acreedor')}</TableCell>
              <TableCell align="right">{t('budgetPlanner.debt.balance', 'Saldo')}</TableCell>
              <TableCell align="right">{t('budgetPlanner.debt.rate', 'Tasa (%)')}</TableCell>
              <TableCell align="right">{t('budgetPlanner.debt.minPayment', 'Pago mín.')}</TableCell>
              <TableCell align="center" sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {state.debts.map((debt) => (
              <TableRow key={debt.id}>
                <TableCell>{debt.creditor}</TableCell>
                <TableCell align="right">{formatCAD(debt.balance)}</TableCell>
                <TableCell align="right">{debt.rate}%</TableCell>
                <TableCell align="right">{formatCAD(debt.minPayment)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => dispatch(removeDebt(debt.id))} color="error" aria-label="Eliminar deuda" sx={{ minWidth: 44, minHeight: 44 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Add new debt */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
        <TextField
          label={t('budgetPlanner.debt.creditor', 'Acreedor')}
          value={form.creditor}
          onChange={(e) => setForm({ ...form, creditor: e.target.value })}
          size="small"
          fullWidth
        />
        <TextField
          label={t('budgetPlanner.debt.balance', 'Saldo ($)')}
          type="number"
          value={form.balance}
          onChange={(e) => setForm({ ...form, balance: e.target.value })}
          size="small"
          sx={{ minWidth: 110 }}
          inputProps={{ min: 0, inputMode: 'decimal' }}
        />
        <TextField
          label={t('budgetPlanner.debt.rate', 'Tasa (%)')}
          type="number"
          value={form.rate}
          onChange={(e) => setForm({ ...form, rate: e.target.value })}
          size="small"
          sx={{ minWidth: 100 }}
          inputProps={{ min: 0, max: 100, step: 0.1, inputMode: 'decimal' }}
        />
        <TextField
          label={t('budgetPlanner.debt.minPayment', 'Pago mín. ($)')}
          type="number"
          value={form.minPayment}
          onChange={(e) => setForm({ ...form, minPayment: e.target.value })}
          size="small"
          sx={{ minWidth: 120 }}
          inputProps={{ min: 0, inputMode: 'decimal' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!form.creditor.trim() || !form.balance || !form.rate || !form.minPayment}
          sx={{ bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' }, minWidth: 100 }}
        >
          {t('budgetPlanner.debt.add', 'Agregar')}
        </Button>
      </Stack>
    </>
  );
}
